/** Codecs between a caller's wire format and the component's `Date` state. */

import {
  formatHm,
  isValidDate,
  parseHm,
  parseYmd,
  setTime,
  ymd,
  type DateRangeValue,
} from "./dateCore";
import type { DateTimePickerCodec, InternalValue, ValueTypeName } from "./types";

/* Scalar conversion */

function parseScalar(raw: unknown, valueType: ValueTypeName, withTime: boolean): Date | undefined {
  if (raw === null || raw === undefined || raw === "") return undefined;

  switch (valueType) {
    case "date":
      return isValidDate(raw) ? raw : undefined;

    case "timestamp": {
      const millis = Number(raw);
      if (!Number.isFinite(millis)) return undefined;
      const date = new Date(millis);
      return isValidDate(date) ? date : undefined;
    }

    case "iso": {
      const text = String(raw);
      // "YYYY-MM-DD" parses as UTC, so date-only strings use the local parser.
      if (!/[T ]\d{1,2}:/.test(text)) return parseYmd(text);
      const date = new Date(text);
      return isValidDate(date) ? date : undefined;
    }

    case "string":
    default: {
      const text = String(raw);
      const date = parseYmd(text);
      if (!date) return undefined;
      if (withTime) {
        const time = /[T ](\d{1,2}):(\d{2})/.exec(text);
        if (time) {
          const hours = Number(time[1]);
          const minutes = Number(time[2]);
          if (hours <= 23 && minutes <= 59) return setTime(date, hours, minutes);
        }
      }
      return date;
    }
  }
}

function serializeScalar(date: Date, valueType: ValueTypeName, withTime: boolean): unknown {
  switch (valueType) {
    case "date":
      return date;
    case "timestamp":
      return date.getTime();
    case "iso":
      return date.toISOString();
    case "string":
    default:
      return withTime
        ? `${ymd(date)}T${formatHm(date.getHours(), date.getMinutes())}`
        : ymd(date);
  }
}

/* Per-variant codecs */

function makeScalarCodec(withTime: boolean): DateTimePickerCodec {
  return {
    parse: (raw, valueType) => parseScalar(raw, valueType, withTime),
    serialize: (value, valueType) =>
      isValidDate(value) ? serializeScalar(value, valueType, withTime) : undefined,
  };
}

/** `variant="single"` -- one calendar day, time discarded. */
export const dateCodec: DateTimePickerCodec = makeScalarCodec(false);

/** `variant="datetime"` -- day plus time of day, preserved through the wire format. */
export const datetimeCodec: DateTimePickerCodec = makeScalarCodec(true);

/** `variant="range"` -- `{ from, to }`, each endpoint a scalar. */
export const rangeCodec: DateTimePickerCodec = {
  parse: (raw, valueType) => {
    if (!raw || typeof raw !== "object") return undefined;
    const source = raw as { from?: unknown; to?: unknown };
    const from = parseScalar(source.from, valueType, false);
    if (!from) return undefined;
    const to = parseScalar(source.to, valueType, false);
    return to ? { from, to } : { from };
  },
  serialize: (value, valueType) => {
    const range = value as DateRangeValue | undefined;
    if (!range?.from || !isValidDate(range.from)) return undefined;
    const from = serializeScalar(range.from, valueType, false);
    if (!isValidDate(range.to)) return { from };
    return { from, to: serializeScalar(range.to, valueType, false) };
  },
};

/** `variant="multiple"` -- an array of days; unparseable entries are dropped. */
export const multipleCodec: DateTimePickerCodec = {
  parse: (raw, valueType) => {
    if (!Array.isArray(raw)) return undefined;
    const dates = raw
      .map((entry) => parseScalar(entry, valueType, false))
      .filter((entry): entry is Date => isValidDate(entry));
    return dates.length > 0 ? dates : undefined;
  },
  serialize: (value, valueType) => {
    if (!Array.isArray(value)) return undefined;
    const serialized = (value as Date[])
      .filter((entry) => isValidDate(entry))
      .map((entry) => serializeScalar(entry, valueType, false));
    return serialized.length > 0 ? serialized : undefined;
  },
};

/** `variant="time"` -- always `"HH:MM"`, the format `ClockTimeInput` emits. */
export const timeCodec: DateTimePickerCodec = {
  parse: (raw) => {
    if (typeof raw !== "string") return undefined;
    const parsed = parseHm(raw);
    return parsed ? formatHm(parsed.hours, parsed.minutes) : undefined;
  },
  serialize: (value) => {
    if (typeof value !== "string") return "";
    const parsed = parseHm(value);
    return parsed ? formatHm(parsed.hours, parsed.minutes) : "";
  },
};

/* Hidden-input serialization */

/** Flatten any value for a hidden input: ranges join with `/`, multiples `,`. */
export function toFormValue(value: InternalValue, withTime = false): string {
  if (value === undefined || value === null) return "";
  if (typeof value === "string") return value;
  if (isValidDate(value)) return String(serializeScalar(value, "string", withTime));
  if (Array.isArray(value)) {
    return value
      .filter((entry) => isValidDate(entry))
      .map((entry) => serializeScalar(entry, "string", withTime))
      .join(",");
  }
  const range = value as DateRangeValue;
  if (!isValidDate(range?.from)) return "";
  const from = ymd(range.from);
  return isValidDate(range.to) ? `${from}/${ymd(range.to)}` : from;
}
